import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Sparkles, RefreshCw, Play, Info, Dices, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { mediaUrl } from '../utils/mediaUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_RATINGS = ['G', 'PG', 'PG13', 'NC16', 'M18', 'R21'];
const DURATIONS = [
  { label: 'Any duration', value: '' },
  { label: 'Short (< 90 min)', value: 'short' },
  { label: 'Medium (90-120 min)', value: 'medium' },
  { label: 'Long (120-150 min)', value: 'long' },
  { label: 'Epic (150+ min)', value: 'epic' }
];
const RATING_RANGES = [
  { label: 'Any rating', value: '' },
  { label: '7.0+', value: '7' },
  { label: '8.0+', value: '8' },
  { label: '9.0+', value: '9' }
];

export const SurpriseMePage = () => {
  const [movies, setMovies] = useState([]);
  const [pickedMovie, setPickedMovie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rolling, setRolling] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const navigate = useNavigate();
  const audioCtxRef = useRef(null);

  // Filter states
  const [filters, setFilters] = useState({
    genre: '',
    language: '',
    ageRating: '',
    duration: '',
    minRating: '',
    year: ''
  });

  const [availableGenres, setAvailableGenres] = useState([]);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    fetchMovies();
  }, []);

  const fetchMovies = async () => {
    try {
      const { data } = await axios.get(`${API}/movies`, { withCredentials: true });
      setMovies(data);
      setAvailableGenres([...new Set(data.flatMap(m => m.genre || []))].sort());
      setAvailableLanguages([...new Set(data.map(m => m.language).filter(Boolean))].sort());
      setAvailableYears([...new Set(data.map(m => m.release_year).filter(Boolean))].sort((a, b) => b - a));
    } catch (error) {
      console.error('Error fetching movies:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterMovies = () => {
    return movies.filter(movie => {
      // Age restriction check - never suggest restricted movies
      if (movie.is_restricted) return false;

      if (filters.genre && !movie.genre?.includes(filters.genre)) return false;
      if (filters.language && movie.language !== filters.language) return false;
      if (filters.ageRating && movie.age_rating !== filters.ageRating) return false;
      if (filters.year && movie.release_year !== parseInt(filters.year)) return false;
      
      if (filters.minRating && (movie.rating || 0) < parseFloat(filters.minRating)) return false;

      if (filters.duration) {
        const runtime = movie.runtime || 0;
        if (filters.duration === 'short' && runtime >= 90) return false;
        if (filters.duration === 'medium' && (runtime < 90 || runtime > 120)) return false;
        if (filters.duration === 'long' && (runtime < 120 || runtime > 150)) return false;
        if (filters.duration === 'epic' && runtime < 150) return false;
      }

      return true;
    });
  };

  /**
   * Play a short "roulette tick" sound synthesized with the Web Audio API.
   * No external assets needed — works offline and instantly.
   * Slows down (like a roulette wheel coming to rest) over ~1 second.
   */
  const playRouletteSound = () => {
    try {
      if (!audioCtxRef.current) {
        // eslint-disable-next-line no-undef
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        audioCtxRef.current = new AudioCtx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();

      const now = ctx.currentTime;
      // 14 ticks that slow down (interval grows) — mimics a roulette wheel
      let t = now;
      const intervals = [0.06, 0.06, 0.07, 0.08, 0.09, 0.1, 0.12, 0.14, 0.16, 0.19, 0.23, 0.28, 0.34, 0.42];
      intervals.forEach((gap, idx) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'square';
        // Pitch drops slightly with each tick
        osc.frequency.setValueAtTime(1200 - idx * 40, t);
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.18, t + 0.005);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t);
        osc.stop(t + 0.06);
        t += gap;
      });
      // Final "ding" landing sound
      const dingT = t + 0.05;
      const dingOsc = ctx.createOscillator();
      const dingGain = ctx.createGain();
      dingOsc.type = 'sine';
      dingOsc.frequency.setValueAtTime(880, dingT);
      dingOsc.frequency.exponentialRampToValueAtTime(1320, dingT + 0.15);
      dingGain.gain.setValueAtTime(0.0001, dingT);
      dingGain.gain.exponentialRampToValueAtTime(0.35, dingT + 0.02);
      dingGain.gain.exponentialRampToValueAtTime(0.0001, dingT + 0.5);
      dingOsc.connect(dingGain).connect(ctx.destination);
      dingOsc.start(dingT);
      dingOsc.stop(dingT + 0.55);
    } catch (e) {
      // Silently ignore audio failures — visual roulette still works
    }
  };

  const surpriseMe = () => {
    const filtered = filterMovies();
    
    if (filtered.length === 0) {
      toast.error('No movies match your filters. Try adjusting them.');
      setPickedMovie(null);
      return;
    }

    setRolling(true);
    setPickedMovie(null);
    playRouletteSound();

    // Animate through several movies before landing on the final one
    let iterations = 0;
    const maxIterations = 12;
    const interval = setInterval(() => {
      const randomMovie = filtered[Math.floor(Math.random() * filtered.length)];
      setPickedMovie(randomMovie);
      iterations++;
      
      if (iterations >= maxIterations) {
        clearInterval(interval);
        // Final pick
        const finalPick = filtered[Math.floor(Math.random() * filtered.length)];
        setPickedMovie(finalPick);
        setRolling(false);
        setShowFilters(false);
      }
    }, 80);
  };

  const resetAll = () => {
    setFilters({
      genre: '',
      language: '',
      ageRating: '',
      duration: '',
      minRating: '',
      year: ''
    });
    setPickedMovie(null);
    setShowFilters(true);
  };

  const filteredCount = filterMovies().length;

  const selectStyle = {
    backgroundColor: '#0F171E',
    border: '1px solid #2A3F52',
    color: '#FFFFFF'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0F171E' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: '#3B82F6' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 px-4 sm:px-8 md:px-12 lg:px-16 pb-12" style={{ backgroundColor: '#0F171E' }} data-testid="surprise-me-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full mb-4" style={{ backgroundColor: 'rgba(240, 193, 75, 0.1)', border: '1px solid #F0C14B' }}>
            <Sparkles size={18} style={{ color: '#F0C14B' }} />
            <span className="text-sm font-medium" style={{ color: '#F0C14B' }}>Random Movie Picker</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-3" style={{ color: '#FFFFFF' }}>
            Surprise Me!
          </h1>
          <p className="text-lg" style={{ color: '#8197A4' }}>
            Can&apos;t decide what to watch? Set your preferences and let us pick.
          </p>
        </div>

        {/* Main Action Button — placed above filters so it's the first thing users see */}
        <div className="text-center mb-8">
          <button
            onClick={surpriseMe}
            disabled={rolling || filteredCount === 0}
            className="inline-flex items-center gap-3 px-10 py-5 rounded-full font-bold text-lg transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
              color: 'white',
              boxShadow: '0 8px 30px rgba(59, 130, 246, 0.4)'
            }}
            data-testid="surprise-me-button"
          >
            <Dices size={24} className={rolling ? 'animate-spin' : ''} />
            {rolling ? 'Picking...' : pickedMovie ? 'Try Another' : 'Surprise Me!'}
          </button>
          <p className="text-xs mt-3" style={{ color: '#8197A4' }}>
            <span className="font-semibold" style={{ color: '#3B82F6' }}>{filteredCount}</span> movies match your criteria — refine below to narrow the pool
          </p>
        </div>

        {/* Filters Section */}
        <div className="rounded-2xl p-6 mb-8" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Filter size={20} style={{ color: '#3B82F6' }} />
              <h2 className="text-xl font-bold" style={{ color: '#FFFFFF' }}>Refine your pick</h2>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="text-sm font-medium md:hidden"
              style={{ color: '#3B82F6' }}
              data-testid="toggle-filters-mobile"
            >
              {showFilters ? 'Hide' : 'Show'}
            </button>
          </div>

          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${showFilters ? '' : 'hidden md:grid'}`}>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#8197A4' }}>Genre</label>
              <select
                value={filters.genre}
                onChange={(e) => setFilters({ ...filters, genre: e.target.value })}
                className="w-full px-4 py-2 rounded-lg outline-none"
                style={selectStyle}
                data-testid="surprise-genre-filter"
              >
                <option value="">Any Genre</option>
                {availableGenres.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#8197A4' }}>Language</label>
              <select
                value={filters.language}
                onChange={(e) => setFilters({ ...filters, language: e.target.value })}
                className="w-full px-4 py-2 rounded-lg outline-none"
                style={selectStyle}
                data-testid="surprise-language-filter"
              >
                <option value="">Any Language</option>
                {availableLanguages.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#8197A4' }}>Age Rating</label>
              <select
                value={filters.ageRating}
                onChange={(e) => setFilters({ ...filters, ageRating: e.target.value })}
                className="w-full px-4 py-2 rounded-lg outline-none"
                style={selectStyle}
                data-testid="surprise-age-filter"
              >
                <option value="">Any Rating</option>
                {AGE_RATINGS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#8197A4' }}>Duration</label>
              <select
                value={filters.duration}
                onChange={(e) => setFilters({ ...filters, duration: e.target.value })}
                className="w-full px-4 py-2 rounded-lg outline-none"
                style={selectStyle}
                data-testid="surprise-duration-filter"
              >
                {DURATIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#8197A4' }}>Minimum Rating</label>
              <select
                value={filters.minRating}
                onChange={(e) => setFilters({ ...filters, minRating: e.target.value })}
                className="w-full px-4 py-2 rounded-lg outline-none"
                style={selectStyle}
                data-testid="surprise-rating-filter"
              >
                {RATING_RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#8197A4' }}>Release Year</label>
              <select
                value={filters.year}
                onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                className="w-full px-4 py-2 rounded-lg outline-none"
                style={selectStyle}
                data-testid="surprise-year-filter"
              >
                <option value="">Any Year</option>
                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
            <p className="text-sm" style={{ color: '#8197A4' }}>
              <span className="font-semibold" style={{ color: '#3B82F6' }}>{filteredCount}</span> movies match your criteria
            </p>
            <button
              onClick={resetAll}
              className="text-sm font-medium hover:underline"
              style={{ color: '#8197A4' }}
              data-testid="reset-filters-button"
            >
              Reset all
            </button>
          </div>
        </div>

        {/* Picked Movie Display */}
        {pickedMovie && (
          <div
            className={`rounded-2xl overflow-hidden transition-all duration-500 ${rolling ? 'opacity-60 scale-95' : 'opacity-100 scale-100'}`}
            style={{ backgroundColor: '#1A2632', border: '2px solid #3B82F6', boxShadow: '0 20px 60px rgba(59, 130, 246, 0.2)' }}
            data-testid="picked-movie-card"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
              <div className="relative">
                <img
                  src={mediaUrl(pickedMovie.poster_path, API) || `https://picsum.photos/seed/${pickedMovie.movie_id}/400/600`}
                  alt={pickedMovie.title}
                  className="w-full h-full object-cover"
                  style={{ minHeight: '400px' }}
                />
                {!rolling && (
                  <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-2 rounded-full" style={{ backgroundColor: 'rgba(240, 193, 75, 0.95)' }}>
                    <Sparkles size={14} style={{ color: '#0F171E' }} />
                    <span className="text-xs font-bold" style={{ color: '#0F171E' }}>Your Pick!</span>
                  </div>
                )}
              </div>

              <div className="md:col-span-2 p-8 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <span className="px-3 py-1 rounded text-xs font-bold" style={{ backgroundColor: '#3B82F6', color: 'white' }}>
                      {pickedMovie.age_rating}
                    </span>
                    <span className="text-sm" style={{ color: '#DDDDDD' }}>{pickedMovie.release_year}</span>
                    <span className="text-sm" style={{ color: '#DDDDDD' }}>{pickedMovie.runtime} min</span>
                    <div className="flex items-center gap-1 px-2 py-1 rounded" style={{ backgroundColor: 'rgba(240, 193, 75, 0.15)' }}>
                      <span className="text-xs font-semibold" style={{ color: '#F0C14B' }}>★ {pickedMovie.rating}</span>
                    </div>
                  </div>

                  <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: '#FFFFFF' }} data-testid="picked-movie-title">
                    {pickedMovie.title}
                  </h2>

                  {/* Primary CTAs — placed directly under the title for instant reach */}
                  <div className="flex gap-3 flex-wrap mb-5">
                    <button
                      onClick={() => navigate(`/movie/${pickedMovie.movie_id}`)}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold transition-all duration-200 hover:scale-105"
                      style={{ backgroundColor: '#FFFFFF', color: '#0F171E' }}
                      data-testid="picked-movie-details-button"
                    >
                      <Info size={18} />
                      View Details
                    </button>
                    <button
                      onClick={surpriseMe}
                      disabled={rolling}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold transition-all duration-200"
                      style={{ backgroundColor: '#2A3F52', color: '#FFFFFF', border: '1px solid #3E5468' }}
                      data-testid="reroll-button"
                    >
                      <RefreshCw size={18} className={rolling ? 'animate-spin' : ''} />
                      Pick Another
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {pickedMovie.genre?.map(g => (
                      <span
                        key={g}
                        className="px-3 py-1 rounded-full text-xs"
                        style={{ backgroundColor: '#2A3F52', color: '#DDDDDD' }}
                      >
                        {g}
                      </span>
                    ))}
                  </div>

                  <p className="text-base leading-relaxed mb-4" style={{ color: '#DDDDDD' }}>
                    {pickedMovie.synopsis}
                  </p>

                  <div className="grid grid-cols-2 gap-3 mb-6 text-sm">
                    <div>
                      <span style={{ color: '#8197A4' }}>Director: </span>
                      <span style={{ color: '#FFFFFF' }}>{pickedMovie.director}</span>
                    </div>
                    <div>
                      <span style={{ color: '#8197A4' }}>Language: </span>
                      <span style={{ color: '#FFFFFF' }}>{pickedMovie.language}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!pickedMovie && !rolling && (
          <div className="text-center py-16 rounded-2xl" style={{ backgroundColor: '#1A2632', border: '1px dashed #2A3F52' }}>
            <Sparkles size={48} className="mx-auto mb-4" style={{ color: '#3B82F6' }} />
            <p className="text-lg font-semibold mb-2" style={{ color: '#FFFFFF' }}>Ready when you are</p>
            <p className="text-sm" style={{ color: '#8197A4' }}>
              Adjust the filters above and hit the button to find your next movie
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
